import { create } from "zustand";

/**
 * An armful going up, and the album it goes into.
 *
 * The phone's `ImportModel` owns its run: the sheet that started it can
 * be dismissed, the run goes on, and the dashboard's status button shows
 * it. The web's run lived in the sheet — closed with the album it was
 * opened in, or with a door to the settings, the sheet took the run's
 * face with it: the photographs kept landing, the diary's chips stayed
 * held with nothing on screen to say why, and there was no Stop and no
 * summary to come back to. The armful lives here now, wherever its sheet
 * is by then; a sheet mounted over the same album shows it as it stands,
 * mid-run or after.
 *
 * The chips read `albumId` and hold still while a run is going — a tap on
 * another album mid-run "is only a way to look at it", as the phone
 * refuses to change the destination — and every sheet's Upload does the
 * same, since one run at a time is all the store holds.
 */
export type PlannedState = "ready" | "sent" | "taken" | "failed";
export type Planned = { file: File; day: string; state: PlannedState };
/** Why a run stopped short on its own: the connection went, or the session did. */
export type Halt = "unreachable" | "signedOut";

export type Armful = {
  albumId: string;
  /** The rows as they stand: the state of each moves as the run goes. */
  plan: Planned[];
  running: boolean;
  /** Stop was pressed: the loop reads this between photographs. */
  stopAsked: boolean;
  /** And the button shows the press landed while the one in flight finishes. */
  stopping: boolean;
  /** The detector's first load stands in front of the first photograph. */
  warming: boolean;
  halt: Halt | null;
  /** A run has ended over this plan: the summary under the rows is its report. */
  ran: boolean;
};

type ImportRun = {
  /** The album a run is going into right now, or null. */
  albumId: string | null;
  /** The armful being run, or the last one run and not yet put away. */
  armful: Armful | null;
  begin: (albumId: string, plan: Planned[], warming: boolean) => void;
  patch: (change: Partial<Armful>) => void;
  stop: () => void;
  end: () => void;
  /** Put the last armful away: Cancel after a run, or a new choice of files. */
  clear: () => void;
  /**
   * The account changes hands: the armful is put away, report and all.
   * The next account in this tab must not find "Added 3 days" on its
   * button for days it never owned. Called by the auth store on a
   * sign-out somebody chose and on the next sign-in — not on the
   * server's verdict that a session is over, which stops a run mid-way
   * and leaves its sheet saying so for the moment the door takes to
   * come; the sign-in that follows puts it away.
   */
  forget: () => void;
};

export const useImportRun = create<ImportRun>((set, get) => ({
  albumId: null,
  armful: null,
  begin: (albumId, plan, warming) =>
    set({
      albumId,
      armful: {
        albumId,
        plan,
        running: true,
        stopAsked: false,
        stopping: false,
        warming,
        halt: null,
        ran: false,
      },
    }),
  patch: (change) => {
    const { armful } = get();
    if (armful) set({ armful: { ...armful, ...change } });
  },
  stop: () => get().patch({ stopAsked: true, stopping: true }),
  end: () => {
    const { armful } = get();
    set({
      albumId: null,
      armful: armful
        ? { ...armful, running: false, stopping: false, warming: false, ran: true }
        : null,
    });
  },
  clear: () => set({ armful: null }),
  forget: () => set({ albumId: null, armful: null }),
}));

/** The state a test starts from. */
export const IMPORT_RUN_IDLE = { albumId: null, armful: null } as const;
